import { adminBarOffset } from './sticky';

/** Breathing room above the form, on top of anything pinned to the top of the viewport. */
export const SCROLL_GAP = 16;

/** Probe depths, in px from the top of the viewport. */
const PROBE_POINTS = [1, 12, 24, 36, 48, 60];

/**
 * How much of the top of the viewport is covered by something pinned there — the host theme's
 * sticky header, the WordPress admin bar. Reads the element stack at a few depths rather than
 * scanning the DOM, so it costs nothing and needs no knowledge of the theme's markup. Several
 * depths because a floating header can be inset from the very top (this site's is a pill at
 * top: 18px, so a single probe at y=1 sails straight past it). Elements taller than 40% of the
 * viewport are page containers, not headers, and our own widget never measures itself.
 */
export function pinnedTopOffset(doc: Document): number {
  const win = doc.defaultView;
  if (!win || typeof doc.elementsFromPoint !== 'function') return 0;
  const x = Math.round((win.innerWidth || 0) / 2);
  let bottom = 0;
  for (const y of PROBE_POINTS) {
    for (const el of doc.elementsFromPoint(x, y) ?? []) {
      if (el.closest?.('.cbf-root')) continue;
      const position = win.getComputedStyle(el).position;
      if (position !== 'fixed' && position !== 'sticky') continue;
      const rect = el.getBoundingClientRect();
      if (rect.height > (win.innerHeight || 0) * 0.4) continue;
      bottom = Math.max(bottom, rect.bottom);
    }
  }
  return Math.max(0, bottom);
}

/**
 * Brings the top of the form back into view after a step change — a customer who presses Next at
 * the bottom of a long step would otherwise land halfway down the next one. Uses the document
 * scroller rather than scrollIntoView so the sticky header, admin bar and gap can be accounted
 * for, and honours prefers-reduced-motion.
 */
export function scrollToFormTop(root: HTMLElement | null): void {
  const doc = root?.ownerDocument;
  const win = doc?.defaultView;
  if (!root || !doc || !win || typeof win.scrollTo !== 'function') return;
  const clearance = () => SCROLL_GAP + Math.max(pinnedTopOffset(doc), adminBarOffset(doc));
  const top = root.getBoundingClientRect().top + (win.scrollY ?? 0) - clearance();
  // Already at or above the form's top: leave the page alone rather than scrolling down to it.
  if ((win.scrollY ?? 0) <= Math.max(0, top)) return;
  const reduced = win.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
  win.scrollTo({ top: Math.max(0, top), behavior: reduced ? 'auto' : 'smooth' });

  /**
   * A header that hides on scroll-down (this site's does) is off-screen while we measure and
   * slides back in once we scroll up, covering what we just brought into view. Re-measure after
   * the scroll settles and top up by whatever now overlaps. A header that is always pinned was
   * already accounted for above, so this finds nothing to do.
   */
  const settle = () => {
    if (typeof win.scrollBy !== 'function') return;
    const overlap = clearance() - root.getBoundingClientRect().top;
    if (overlap > 1) win.scrollBy({ top: -overlap, behavior: 'auto' });
  };
  if (typeof win.addEventListener === 'function' && 'onscrollend' in win) {
    win.addEventListener('scrollend', settle, { once: true });
  } else if (typeof win.setTimeout === 'function') {
    win.setTimeout(settle, 600);
  }
}
